import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "441",
  "slug": "what-is-the-authagraph-map-projection",
  "title": "What Is the AuthaGraph Map Projection?",
  "excerpt": "A Japanese designer folded the globe into a tetrahedron and unfolded it into a rectangle, creating one of the most balanced world maps ever made. Here is how the AuthaGraph projection works.",
  "date": "2026-06-14",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1521295121783-8a321d551ad2?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx3b3JsZCUyMG1hcCUyMGdsb2JlfGVufDF8MHx8fDE3ODE0MjUwODJ8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "AuthaGraph is a modern world map projection created by Japanese architect Hajime Narukawa. By dividing the globe into 96 regions and mapping them onto a tetrahedron that unfolds into a rectangle, it keeps the relative sizes of land and sea far more faithful than the familiar Mercator map.",
  "content": [
    {
      "type": "paragraph",
      "text": "Every flat map of the round Earth has to cheat somewhere. You cannot peel a sphere onto a rectangle without stretching, squashing, or tearing something, so every projection makes a trade-off between accurate shapes, accurate sizes, and accurate distances. The AuthaGraph projection is a clever modern attempt to keep all of those trade-offs as small as possible, and the result looks unlike any map most people grew up with."
    },
    {
      "type": "heading",
      "text": "The problem it set out to solve"
    },
    {
      "type": "paragraph",
      "text": "The world map most of us picture is the Mercator projection, which is excellent for navigation but badly distorts size. It makes high-latitude regions look enormous: Greenland appears roughly the size of Africa, even though Africa is about fourteen times larger. Designers have spent over a century inventing alternatives that keep areas honest, and AuthaGraph is one of the most ambitious."
    },
    {
      "type": "heading",
      "text": "How it works"
    },
    {
      "type": "paragraph",
      "text": "AuthaGraph was created by the Japanese architect Hajime Narukawa, who first developed it in the late 1990s. The method is genuinely inventive:"
    },
    {
      "type": "list",
      "items": [
        "The surface of the globe is divided into 96 equal triangular regions.",
        "Those regions are projected onto a tetrahedron, a four-sided pyramid shape.",
        "The tetrahedron is then unfolded flat and trimmed into a rectangle."
      ]
    },
    {
      "type": "paragraph",
      "text": "Because the distortion is spread out across the whole map rather than piled up at the edges, the relative sizes of continents and oceans stay close to correct, and their shapes are reasonably preserved too. Antarctica, usually smeared into a giant white band along the bottom of a Mercator map, appears as a normal-looking continent."
    },
    {
      "type": "heading",
      "text": "What makes it unusual"
    },
    {
      "type": "paragraph",
      "text": "AuthaGraph has no fixed top or center. Because it is built from an unfolded shape that can be tiled, you can repeat it and crop a new rectangle from anywhere, putting any region you like at the middle. That breaks the habit of always placing one particular country or ocean at the center of the world, and it can make even a familiar planet feel surprisingly fresh."
    },
    {
      "type": "paragraph",
      "text": "It is worth being precise about the claims. AuthaGraph is often described as area-accurate, but it is more correct to say it is very nearly so, a close approximation rather than a perfectly equal-area map. Some distortion always remains; the achievement is how evenly it is distributed. The design earned wide recognition, including a top Japanese design award in 2016."
    },
    {
      "type": "heading",
      "text": "How it compares to other maps"
    },
    {
      "type": "paragraph",
      "text": "AuthaGraph is part of a long tradition of mapmakers trying to fix Mercator's size distortion, and it helps to see where it sits among them. The Gall-Peters projection keeps areas strictly accurate but badly stretches shapes, making continents look squashed. The Equal Earth projection, a more recent design, keeps sizes honest while looking pleasant and familiar. Buckminster Fuller's Dymaxion map, like AuthaGraph, unfolds the globe onto a polyhedron, but it leaves the world in a jagged, scattered shape rather than a tidy rectangle. AuthaGraph's distinctive contribution is fitting a near-balanced world into a clean, tileable rectangle."
    },
    {
      "type": "heading",
      "text": "The catches"
    },
    {
      "type": "paragraph",
      "text": "No projection is free, and AuthaGraph has trade-offs of its own. Its unfamiliar layout can be disorienting at first, since directions are not simply up-is-north across the whole map. It is also not designed for navigation or for reading off precise coordinates, the everyday job Mercator still does well. And because it is a proprietary design, it is less freely reproduced than older public-domain projections. It is best understood as a tool for seeing the whole planet in fair proportion, not as a replacement for every map at once."
    },
    {
      "type": "heading",
      "text": "Why projections matter for map skills"
    },
    {
      "type": "paragraph",
      "text": "Understanding projections changes how you read every map you see. Once you know that the familiar rectangle is a distortion, you start questioning which countries really are bigger, which routes really are shorter, and how much a map is shaping your mental picture of the world. That kind of spatial awareness is exactly what makes someone good at reading the planet."
    }
  ]
};
